import React, { useRef, useEffect } from "react";
import { ApiAdminUser } from "utils/api/admin/user";
import { ApiAdminRepaymentInstance } from "utils/api/admin/repaymentInstance";
import { Heading } from "components/ui/Text";
import DebitExternalAccountModal from "components/modal/DebitExternalAccountModal";
import CreateRepaymentScheduleModal from "components/modal/CreateRepaymentScheduleModal";
import Icon from "components/ui/Icon";
import { ModalApi } from "components/modal/Modal";
import { sortByDateAscending } from "utils/api/array";
import {
  useGetAdminRepaymentSchedulesQuery,
  usePostAdminRepaymentInstanceCancelMutation,
  usePostAdminRepaymentScheduleInactivateMutation,
  usePostAdminRepaymentScheduleApproveMutation,
} from "store/admin/api/apiSlice";
import Loading from "components/ui/Loading";
import { getDayOfWeek } from "utils/date";
import * as Styled from "./styled";

interface RepaymentInstanceDetailsProps {
  instance: ApiAdminRepaymentInstance;
  showCancelButton?: boolean;
  onCancel?: (id: string) => void;
  isCancelling?: boolean;
}

const RepaymentInstanceDetails: React.FC<RepaymentInstanceDetailsProps> = ({
  instance,
  showCancelButton = false,
  onCancel,
  isCancelling = false,
}) => (
  <Styled.InfoSection>
    <Styled.InfoItem label={`Instance ID:`} value={instance.id} />
    <Styled.InfoItem label={`Effective Date:`} value={instance.effectiveDate} />
    <Styled.InfoItem label={`Created At:`} value={instance.createdAt} />
    {showCancelButton && onCancel && (
      <Styled.DangerGhostButton
        onClick={() => onCancel(instance.id)}
        disabled={isCancelling}
      >
        Cancel
      </Styled.DangerGhostButton>
    )}
  </Styled.InfoSection>
);

interface RepaymentInstancesListProps {
  user: ApiAdminUser;
  refetchUser: () => void;
}

const RepaymentInstancesList: React.FC<RepaymentInstancesListProps> = ({
  user,
  refetchUser,
}) => {
  const [cancelInstance, { isLoading: isCancelling, isSuccess }] =
    usePostAdminRepaymentInstanceCancelMutation();
  const { scheduledRepaymentInstances } = user;

  const sortedScheduledRepaymentInstances = sortByDateAscending(
    scheduledRepaymentInstances,
    "effectiveDate"
  );

  const handleCancelInstance = (id: string) => {
    cancelInstance(id);
  };

  useEffect(() => {
    if (isSuccess) {
      refetchUser();
    }
  }, [isSuccess, refetchUser]);

  if (
    !sortedScheduledRepaymentInstances ||
    sortedScheduledRepaymentInstances.length === 0
  ) {
    return (
      <Styled.Info>
        <Styled.InfoSection>
          <Heading.H4 tag="h3">No Scheduled Repayment Instances</Heading.H4>
        </Styled.InfoSection>
      </Styled.Info>
    );
  }

  return (
    <>
      <Heading.H4 tag="h3">Upcoming Scheduled Repayment Instances</Heading.H4>
      <Styled.Info>
        {sortedScheduledRepaymentInstances.map((instance) => (
          <RepaymentInstanceDetails
            key={instance.id}
            instance={instance}
            showCancelButton={true}
            onCancel={handleCancelInstance}
            isCancelling={isCancelling}
          />
        ))}
      </Styled.Info>
    </>
  );
};

interface RepaymentSchedulesListProps {
  user: ApiAdminUser;
  refetchUser?: () => void;
}

const RepaymentSchedulesList: React.FC<RepaymentSchedulesListProps> = ({
  user,
  refetchUser,
}) => {
  const {
    data: schedules,
    isLoading,
    refetch: refetchSchedules,
  } = useGetAdminRepaymentSchedulesQuery(user.id);
  const [
    inactivateSchedule,
    { isLoading: isInactivating, isSuccess: isInactivateSuccess },
  ] = usePostAdminRepaymentScheduleInactivateMutation();
  const [
    approveSchedule,
    { isLoading: isApproving, isSuccess: isApproveSuccess },
  ] = usePostAdminRepaymentScheduleApproveMutation();

  const handleInactivate = (id: string) => {
    inactivateSchedule(id);
  };

  const handleApprove = (id: string) => {
    approveSchedule(id);
  };

  useEffect(() => {
    if (isInactivateSuccess || isApproveSuccess) {
      refetchSchedules();
      if (refetchUser) {
        refetchUser();
      }
    }
  }, [isInactivateSuccess, isApproveSuccess, refetchSchedules, refetchUser]);

  if (isLoading) {
    return <Loading />;
  }

  const activeSchedules =
    schedules?.filter((schedule) => schedule.active) || [];

  if (activeSchedules.length === 0) {
    return (
      <Styled.Info>
        <Styled.InfoSection>
          <Heading.H4 tag="h3">No Active Repayment Schedules</Heading.H4>
        </Styled.InfoSection>
      </Styled.Info>
    );
  }

  return (
    <>
      <Heading.H4 tag="h3">Active Repayment Schedules</Heading.H4>
      <Styled.Info>
        {activeSchedules.map((schedule) => (
          <Styled.InfoSection key={schedule.id}>
            <Styled.InfoItem
              label={`Schedule Type:`}
              value={schedule.scheduleType}
            />
            <Styled.InfoItem
              label={`Active:`}
              value={schedule.active ? "Yes" : "No"}
            />
            <Styled.InfoItem
              label={`Approval Status:`}
              value={schedule.approvalStatus}
            />
            {schedule.recurringDayOfMonth && (
              <Styled.InfoItem
                label={`Recurring Day of Month:`}
                value={schedule.recurringDayOfMonth}
              />
            )}
            {schedule.recurringDayOfWeek && (
              <Styled.InfoItem
                label={`Recurring Day of Week:`}
                value={getDayOfWeek(schedule.recurringDayOfWeek)}
              />
            )}
            {schedule.recurringWeekOfMonth && (
              <Styled.InfoItem
                label={`Recurring Week of Month:`}
                value={schedule.recurringWeekOfMonth}
              />
            )}
            {schedule.previousBiWeeklyPayDate && (
              <Styled.InfoItem
                label={`Previous Bi-Weekly Pay Date:`}
                value={schedule.previousBiWeeklyPayDate}
              />
            )}
            <Styled.InfoItem label={`Created At:`} value={schedule.createdAt} />
            <Styled.InfoItem label={`Updated At:`} value={schedule.updatedAt} />
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              {schedule.approvalStatus === "pending" && (
                <Styled.DangerGhostButton
                  onClick={() => handleApprove(schedule.id)}
                  disabled={isApproving}
                >
                  Approve
                </Styled.DangerGhostButton>
              )}
              <Styled.DangerGhostButton
                onClick={() => handleInactivate(schedule.id)}
                disabled={isInactivating}
              >
                Inactivate
              </Styled.DangerGhostButton>
            </div>
          </Styled.InfoSection>
        ))}
      </Styled.Info>
    </>
  );
};

interface Props {
  user: ApiAdminUser;
  refetchUser: () => void;
}

const Repayments: React.FC<Props> = ({ user, refetchUser }) => {
  const { repaymentInfo, payDates } = user;
  const {
    lastRepayment,
    lastReversedAchDebitRepayment,
    lastNotReversedAchDebitRepayment,
  } = repaymentInfo;
  const debitExternalAccountModalRef = useRef<ModalApi>(null);
  const createRepaymentScheduleModalRef = useRef<ModalApi>(null);
  const { refetch: refetchSchedules } = useGetAdminRepaymentSchedulesQuery(
    user.id
  );

  const onClickDebitExternalAccount = () => {
    debitExternalAccountModalRef.current?.show();
  };

  const onClickCreateRepaymentSchedule = () => {
    createRepaymentScheduleModalRef.current?.show();
  };

  return (
    <>
      <Styled.SectionTile>
        {user.userConfig.customRepaymentPlan && (
          <div
            style={{
              color: "#dc2626",
              fontWeight: "bold",
              fontSize: "18px",
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
            }}
          >
            ⚠️ CUSTOM REPAYMENT PLAN ENABLED
          </div>
        )}
        <Styled.Info>
          <Styled.InfoSection>
            <Heading.H4 tag="h2">Repayments</Heading.H4>

            <Styled.InfoItem
              label="Number of Repayments"
              value={repaymentInfo.numRepayments || 0}
            />
            <Styled.InfoItem
              label="Last Repayment Amount"
              value={lastRepayment?.amount || "n/a"}
            />
            <Styled.InfoItem
              label="Last Repayment Initiated At"
              value={lastRepayment?.networkStateInitiatedAt || "n/a"}
            />
            <Styled.InfoItem
              label="Last Repayment Settled At"
              value={lastRepayment?.networkStateSettledAt || "n/a"}
            />
            <Styled.InfoItem
              label="Last Reversed Repayment Reversed At"
              value={lastRepayment?.networkStateReversedAt || "n/a"}
            />
            <Styled.InfoItem
              label="Last Repayment Created At"
              value={lastRepayment?.createdAt || "n/a"}
            />
          </Styled.InfoSection>

          <Styled.InfoSection>
            <Heading.H4 tag="h3">Reversed</Heading.H4>

            <Styled.InfoItem
              label="Number of Reversed Repayments"
              value={repaymentInfo.numReversedAchDebitRepayments || 0}
            />
            <Styled.InfoItem
              label="Last Reversed Repayment Amount"
              value={lastReversedAchDebitRepayment?.amount || "n/a"}
            />
            <Styled.InfoItem
              label="Last Reversed Repayment Initiated At"
              value={
                lastReversedAchDebitRepayment?.networkStateInitiatedAt || "n/a"
              }
            />
            <Styled.InfoItem
              label="Last Reversed Repayment Reversed At"
              value={
                lastReversedAchDebitRepayment?.networkStateReversedAt || "n/a"
              }
            />
            <Styled.InfoItem
              label="Last Reversed Repayment Created At"
              value={lastReversedAchDebitRepayment?.createdAt || "n/a"}
            />
            <Styled.InfoItem
              label="Last Reversed Repayment At"
              value={repaymentInfo.lastReversedAchDebitAt || "n/a"}
            />
          </Styled.InfoSection>

          <Styled.InfoSection>
            <Heading.H4 tag="h3">Not Reversed</Heading.H4>

            <Styled.InfoItem
              label="Number of Not Reversed Repayments"
              value={repaymentInfo.numNotReversedAchDebitRepayments || 0}
            />
            <Styled.InfoItem
              label="Last Not Reversed Repayment Amount"
              value={lastNotReversedAchDebitRepayment?.amount || "n/a"}
            />
            <Styled.InfoItem
              label="Last Not Reversed Repayment Initiated At"
              value={
                lastNotReversedAchDebitRepayment?.networkStateInitiatedAt ||
                "n/a"
              }
            />
            <Styled.InfoItem
              label="Last Not Reversed Repayment Settled At"
              value={
                lastNotReversedAchDebitRepayment?.networkStateSettledAt || "n/a"
              }
            />
            <Styled.InfoItem
              label="Last Not Reversed Repayment Created At"
              value={lastNotReversedAchDebitRepayment?.createdAt || "n/a"}
            />
            <Styled.InfoItem
              label="Last Not Reversed Repayment At"
              value={repaymentInfo.lastNotReversedAchDebitAt || "n/a"}
            />
          </Styled.InfoSection>

          {payDates && (
            <Styled.InfoSection>
              <Heading.H4 tag="h3">Projected Pay Dates</Heading.H4>

              {payDates.systemChosenDebitDate && (
                <Styled.InfoItem
                  label="System Chosen Debit Date"
                  value={payDates.systemChosenDebitDate || "n/a"}
                />
              )}
              {payDates.paveDerived && (
                <Styled.InfoItem
                  label="Pave Derived Pay Date"
                  value={payDates.paveDerived || "n/a"}
                />
              )}
              {payDates.pinwheelDerived && (
                <Styled.InfoItem
                  label="Pinwheel Derived Pay Date"
                  value={payDates.pinwheelDerived || "n/a"}
                />
              )}
              {payDates.resetPaystubDerived && (
                <Styled.InfoItem
                  label="Reset Paystub Derived Pay Date"
                  value={payDates.resetPaystubDerived || "n/a"}
                />
              )}
              {payDates.payrollAccountPayPeriodStartDateDerived && (
                <Styled.InfoItem
                  label="Pay Period Start Date Derived Pay Date"
                  value={
                    payDates.payrollAccountPayPeriodStartDateDerived || "n/a"
                  }
                />
              )}
            </Styled.InfoSection>
          )}
        </Styled.Info>

        <RepaymentInstancesList user={user} refetchUser={refetchUser} />

        {user.lastProcessedRepaymentInstance && (
          <>
            <Heading.H4 tag="h3">Last Processed Repayment Instance</Heading.H4>
            <Styled.Info>
              <RepaymentInstanceDetails
                instance={user.lastProcessedRepaymentInstance}
                showCancelButton={false}
              />
            </Styled.Info>
          </>
        )}

        {user.lastCancelledRepaymentInstance && (
          <>
            <Heading.H4 tag="h3">Last Cancelled Repayment Instance</Heading.H4>
            <Styled.Info>
              <RepaymentInstanceDetails
                instance={user.lastCancelledRepaymentInstance}
                showCancelButton={false}
              />
            </Styled.Info>
          </>
        )}

        <RepaymentSchedulesList user={user} refetchUser={refetchUser} />

        <Styled.TileActions>
          <Styled.DangerGhostButton onClick={onClickDebitExternalAccount}>
            <Icon name="transfer" />
            Debit External Account
          </Styled.DangerGhostButton>
          <Styled.DangerGhostButton onClick={onClickCreateRepaymentSchedule}>
            <Icon name="calendar" />
            Create Repayment Schedule
          </Styled.DangerGhostButton>
        </Styled.TileActions>
      </Styled.SectionTile>

      <DebitExternalAccountModal
        ref={debitExternalAccountModalRef}
        onSuccess={refetchUser}
        id={user.id}
      />
      <CreateRepaymentScheduleModal
        ref={createRepaymentScheduleModalRef}
        onSuccess={refetchSchedules}
        user={user}
      />
    </>
  );
};

export default Repayments;
